<script setup lang="ts">

import QuestionListWidget from "@/components/questionFields/widgets/QuestionListWidget.vue";
import ExpressionEditUtility from "@/utils/common/ExpressionEditUtility.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import {
  addRefToList,
  commonCanReceiveFocus,
  commonFocus,
  commonNavigateToNext,
  NamaNavigateToNextEmit,
  NamaNavigationArgs,
  NamaVueWidgetContainer
} from "@/utils/common/NamaVueWidget.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import QuestionField = namaTypes.QuestionField;

const props = defineProps<{
  presenter: QuestionFieldsPresenter | undefined,
  doNotApplyThemeLayouts: boolean,
  singleColumnGroup?: boolean
}>()

let questionFields: QuestionField[] = props.presenter ? props.presenter.fetchQuestionFields() : [];
let refList: NamaVueWidgetContainer[] = [];
const emit = defineEmits<NamaNavigateToNextEmit>()

function isSpanned(questionField: QuestionField): boolean {
  return FieldWidgetUtil.isSpanned(questionField);
}

function isSpanned2(questionField: QuestionField): boolean {
  if (questionField.layout !== EditScreenFieldLayout.spanned2)
    return false;
  return questionField.metaData?.fieldType === FieldType.BigText;
}

function onNavigateToNext(args: NamaNavigationArgs, index: number) {
  commonNavigateToNext(refList, args, index, () => emit('navigateToNext', args));
}

function focus(args: NamaNavigationArgs) {
  commonFocus(refList, args, () => emit('navigateToNext', args));
}

function onRef(widget: any, index: number) {
  refList = addRefToList(refList, {widget, index});
}

function canReceiveFocus(): boolean {
  return commonCanReceiveFocus(refList);
}

defineExpose({focus, canReceiveFocus});
</script>

<template>
  <div v-if="presenter" class="tw-flex tw-flex-wrap tw-gap-x-[4%] compact:tw-gap-x-[1%] ultra:tw-gap-x-[0.5%] tw-items-center"
       :class="{'quad:tw-gap-x-[1%] quad:compact:tw-gap-x-[0.5%] quad:ultra:tw-gap-x-[0.25%] horizontal:tw-gap-x-[0%]':!doNotApplyThemeLayouts}">
    <template v-for="(questionField, index) in questionFields">
      <template v-if="!questionField.hidden">
        <div class="tw-mb-2 mobile:tw-min-w-[99%] compact:tw-mb-0.5 ultra:tw-mb-0.5"
             :class="[doNotApplyThemeLayouts ?
               {
                 'tw-w-[48%] compact:tw-w-[49.5%] ultra:tw-w-[49.5%]':(!singleColumnGroup || !isSpanned(questionField)),
                 'tw-min-w-full':(singleColumnGroup||isSpanned(questionField)),
                 'tw-min-h-[60px] compact:tw-min-h-fit ultra:tw-min-h-fit':!isSpanned2(questionField),
                 'tw-min-h-[136px]':isSpanned2(questionField)
               }: {
                 'tw-w-[48%] compact:tw-w-[49.5%] ultra:tw-w-[49.5%] quad:tw-w-[24%] quad:compact:tw-w-[24.5%] quad:ultra:tw-w-[24.5%] horizontal:tw-w-[50%] horizontal:quad:tw-w-[25%]':(!singleColumnGroup && !isSpanned(questionField) && !isSpanned2(questionField)),
                 'tw-min-w-full quad:horizontal:tw-min-w-full quad:compact:tw-min-w-[99.5%] quad:ultra:tw-min-w-[99.5%] quad:tw-min-w-[99%]':(singleColumnGroup || isSpanned(questionField) || isSpanned2(questionField)),
                 'tw-min-h-[60px] horizontal:tw-min-h-[45px] horizontal:compact:tw-py-0.5 horizontal:ultra:tw-py-0 horizontal:compact:tw-min-h-fit horizontal:ultra:tw-min-h-fit compact:tw-min-h-fit ultra:tw-min-h-fit':!isSpanned2(questionField),
                 'tw-min-h-[136px]':isSpanned2(questionField)
               }
          ]">
          <template v-if="questionField.list && !questionField.doNotAutoShowList">
            <QuestionListWidget
              :presenter="presenter as QuestionFieldsPresenter"
              :questionField="questionField"
              :doNotApplyThemeLayouts="doNotApplyThemeLayouts"/>
          </template>
          <template v-else>
            <component
              :is="ExpressionEditUtility.getWidget(questionField, presenter)"
              :field="questionField" :isInsideComposite="false" :fieldMetaData="questionField.metaData!"
              :presenter="presenter" :doNotApplyThemeLayouts="doNotApplyThemeLayouts" :forceEnableField="true"
              :isSpanned="isSpanned(questionField)"
              :ref="(r:any)=>onRef(r,index)" @navigateToNext="(args)=>onNavigateToNext(args,index)"/>
          </template>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>

</style>
